<template>
	<div class="sii_step">
		<div
			class="sii_card"
			:class="{
				'is-error': state === 'error',
				'is-success': state === 'success',
			}"
		>
			<span class="sii_logo_badge">
				<img :src="siiLogo" alt="SII" />
			</span>
			<template v-if="state === 'idle'">
				<h1 class="sii_title">Conecta tu cuenta del SII</h1>
				<p class="sii_text">
					Nuestros robots automatizarán tus ventas, compras y honorarios.
				</p>
				<button
					class="fintoc_button"
					type="button"
					:disabled="widgetIsLoading"
					@click="connectSii"
				>
					<span>Conectar con</span>
					<FintocLogo class="fintoc_button-logo" />
				</button>
			</template>
			<template v-else-if="state === 'error'">
				<h1 class="sii_title">No pudimos conectar con el SII 😕</h1>
				<p class="sii_text">
					Ocurrió un problema al verificar tu información. Inténtalo
					nuevamente.
				</p>
				<div class="sii_attempts">
					<div class="sii_attempts-dots" aria-hidden="true">
						<span
							v-for="n in maxAttempts"
							:key="n"
							class="sii_attempts-dot"
							:class="{ 'is-used': n <= attemptsShown }"
						></span>
					</div>
					<span class="sii_attempts-label">
						Intento {{ attemptsShown }} de {{ maxAttempts }}
					</span>
				</div>
				<button
					class="state_button is-error"
					type="button"
					:disabled="widgetIsLoading"
					@click="connectSii"
				>
					Reintentar
				</button>
				<div v-if="canSkip" class="sii_skip">
					<button class="sii_skip-button" type="button" @click="skipSii">
						Continuar de todos modos
					</button>
					<span class="sii_skip-hint">
						Podrás conectar el SII más tarde desde tu panel.
					</span>
				</div>
			</template>
			<template v-else>
				<h1 class="sii_title">Conexión exitosa con el SII 🎉</h1>
				<p class="sii_text">
					Hemos verificado tu información tributaria correctamente.
				</p>
				<button
					class="state_button is-success"
					type="button"
					@click="emitContinue"
				>
					Continuar
				</button>
			</template>
		</div>
		<!-- Simulador dev-only: el sandbox fiscal/SII de Fintoc no siempre
		responde en local, así que esto permite validar los estados success/error
		y el botón Continuar sin abrir el widget. No se renderiza en producción. -->
		<div v-if="isDev" class="sii_devtools">
			<span class="sii_devtools-label">dev · simular estado</span>
			<button type="button" @click="state = 'success'">✓ éxito</button>
			<button type="button" @click="state = 'error'">✕ error</button>
			<button type="button" @click="state = 'idle'">↺ idle</button>
			<button type="button" @click="attempts += 1">+ intento</button>
		</div>
	</div>
</template>

<script>
import { getFintocToken, saveFintocUserHolder } from "@/api/fintoc";
import { refresh } from "@/api/security";
import siiLogo from "@/assets/images/sii.svg";
import FintocLogo from "@/components/svg/FintocLogo.vue";
import config from "@/config";
import { getSession, setSession } from "@/utils/auth";
import { consoleHandlerError } from "@/utils/message";

const PRODUCT_TYPE = "invoices";
// Tras MAX_ATTEMPTS conexiones fallidas habilitamos "Continuar de todos modos":
// el widget del SII (vía Fintoc) es inestable y no debe dejar al usuario
// atascado. El SII queda conectable más tarde desde el panel.
const MAX_ATTEMPTS = 3;

export default {
	name: "RegisterSiiConnectStep",
	components: {
		FintocLogo,
	},
	data() {
		return {
			state: "idle",
			widgetIsLoading: false,
			widget: null,
			attempts: 0,
			maxAttempts: MAX_ATTEMPTS,
			siiLogo,
		};
	},
	computed: {
		// Gate del simulador: vue-cli expone NODE_ENV; en build de producción
		// el bloque queda fuera (tree-shaken por el v-if siempre falso).
		isDev() {
			return process.env.NODE_ENV === "development";
		},
		// Habilita el escape recién tras agotar los intentos.
		canSkip() {
			return this.attempts >= this.maxAttempts;
		},
		// Topea el contador: tras el umbral se puede seguir reintentando,
		// pero el medidor queda lleno (N de N).
		attemptsShown() {
			return Math.min(this.attempts, this.maxAttempts);
		},
	},
	beforeDestroy() {
		if (this.widget) {
			this.widget.destroy();
		}
	},
	methods: {
		async connectSii() {
			// Cada click (idle o reintento) cuenta como intento: cubre fallo de
			// token/creación, cierre del widget sin éxito y fallo del guardado.
			this.attempts += 1;
			this.widgetIsLoading = true;
			try {
				const { data } = await getFintocToken({
					product_type: PRODUCT_TYPE,
				});
				this.widget = window.Fintoc.create({
					holderType: "business",
					product: PRODUCT_TYPE,
					publicKey: data.public_key,
					webhookUrl: `${config.webhookAPI}/fintoc_app/link_token/?token=${data.webhook_token}`,
					onExit: () => this.onWidgetExit(),
					onSuccess: (link) =>
						this.onWidgetSuccess(
							data.company_product_link.id,
							link
						),
				});
				this.widget.open();
			} catch (error) {
				consoleHandlerError({
					error,
					silent: true,
					prefix: "[register]",
				});
				this.state = "error";
			} finally {
				this.widgetIsLoading = false;
			}
		},
		async onWidgetSuccess(companyProductLinkId, link) {
			const payload = {
				...link,
				company_product_link_id: companyProductLinkId,
			};
			try {
				await this.saveHolderWithFreshToken(payload);
				// Tras activar el link, el token vigente aún no trae el claim
				// company_product_link_sii; refrescamos y persistimos la sesión
				// para los pasos siguientes del onboarding.
				await this.refreshSession();
			} catch (error) {
				// El éxito lo confirma Fintoc (WIDGET_SUCCEEDED) y el link queda
				// activado server-side; si el save/refresh del cliente falla no
				// bloqueamos el onboarding (solo lo registramos).
				consoleHandlerError({
					error,
					silent: true,
					prefix: "[register]",
				});
			}
			this.state = "success";
		},
		// El webhook de Fintoc (producto invoices) blacklista el token actual al
		// entregar el link_token, así que el save puede llegar con un token ya
		// invalidado y responder 403. skipAuthRedirect evita que el interceptor
		// global haga el redirect duro a /go-to-plan (que expulsaba el wizard al
		// dashboard); ante 403 refrescamos y reintentamos el save una sola vez.
		async saveHolderWithFreshToken(payload) {
			try {
				await saveFintocUserHolder(payload, { skipAuthRedirect: true });
			} catch (error) {
				if (error?.response?.status !== 403) throw error;
				await this.refreshSession();
				await saveFintocUserHolder(payload, { skipAuthRedirect: true });
			}
		},
		async refreshSession() {
			const session = getSession();
			const { data } = await refresh(session.token, {
				skipAuthRedirect: true,
			});
			setSession(data);
		},
		onWidgetExit() {
			if (this.widget) {
				this.widget.destroy();
				this.widget = null;
			}
		},
		skipSii() {
			this.$emit("continue", { sii_connected: false });
		},
		emitContinue() {
			this.$emit("continue", { sii_connected: true });
		},
	},
};
</script>

<style lang="scss" scoped>
// Panel dev-only; estética deliberadamente "fuera de marca" para no confundirlo
// con UI real. No se compila en producción (v-if isDev).
.sii_devtools {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 16px;
	padding: 8px 12px;
	border: 1px dashed var(--auth-border-soft);
	border-radius: 8px;

	button {
		padding: 4px 10px;
		border: 1px solid var(--auth-border-soft);
		border-radius: 6px;
		background: var(--auth-surface);
		color: var(--auth-text-secondary);
		font-size: 12px;
		cursor: pointer;
	}
}

.sii_devtools-label {
	margin-right: auto;
	color: var(--auth-text-muted);
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.sii_card {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 96px 32px;
	background: var(--auth-step-card-bg);
	border-radius: 16px;
	text-align: center;

	&.is-error {
		background: var(--auth-danger-soft);
	}

	&.is-success {
		background: var(--auth-success-soft);
	}
}

.sii_logo_badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 64px;
	height: 64px;
	margin-bottom: 24px;
	background: var(--auth-surface);
	border-radius: 14px;
	box-shadow: 0 1px 3px rgba(22, 36, 47, 0.12);

	img {
		width: 38px;
		height: auto;
	}
}

.sii_title {
	color: var(--auth-text);
	font-size: 18px;
	font-weight: 700;
	margin-bottom: 10px;
}

.sii_text {
	color: var(--auth-text-secondary);
	font-size: 14px;
	line-height: 1.45;
	max-width: 260px;
	margin-bottom: 28px;
}

.fintoc_button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	max-width: 310px;
	height: 44px;
	background: var(--auth-contrast);
	border: none;
	border-radius: 8px;
	color: var(--auth-surface);
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: background-color 0.15s ease-out;

	&:hover:not(:disabled) {
		background: var(--auth-contrast-hover);
	}

	&:disabled {
		opacity: 0.6;
		cursor: wait;
	}
}

// El SVG del logo trae fills hardcodeados (ink/azul); sobre el botón negro
// se invierte a blanco (mismo recurso que .iconWhite en SelectComponent).
.fintoc_button-logo {
	width: 64px;
	height: auto;
	filter: brightness(0) invert(1);
}

.state_button {
	width: 100%;
	max-width: 310px;
	height: 44px;
	border: none;
	border-radius: 8px;
	color: var(--auth-surface);
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: opacity 0.15s ease-out;

	&:hover {
		opacity: 0.9;
	}

	&.is-error {
		background: var(--auth-danger);
	}

	&.is-success {
		background: var(--auth-accent);
	}
}

// Medidor de intentos: gris pizarra, NO teal — el teal en este onboarding
// significa progreso/éxito y un intento fallido no es progreso. Susurra.
.sii_attempts {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	margin-bottom: 24px;
}

.sii_attempts-dots {
	display: flex;
	gap: 8px;
}

.sii_attempts-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	border: 1px solid var(--auth-border);
	background: transparent;
	transition: background-color 0.2s ease-out, border-color 0.2s ease-out;

	&.is-used {
		background: var(--auth-text-secondary);
		border-color: var(--auth-text-secondary);
	}
}

.sii_attempts-label {
	color: var(--auth-text-muted);
	font-size: 12px;
}

// Escape contextual: aparece recién tras el umbral, separado por una línea
// suave. Ghost (no color de marca) para leerse como alternativa calma, no CTA.
.sii_skip {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	width: 100%;
	max-width: 310px;
	margin-top: 20px;
	padding-top: 20px;
	border-top: 1px solid var(--auth-border-soft);
}

.sii_skip-button {
	width: 100%;
	height: 44px;
	background: transparent;
	border: 1px solid var(--auth-control-border);
	border-radius: 8px;
	color: var(--auth-text-secondary);
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: border-color 0.15s ease-out, color 0.15s ease-out;

	&:hover {
		border-color: var(--auth-text-secondary);
		color: var(--auth-text);
	}
}

.sii_skip-hint {
	color: var(--auth-text-muted);
	font-size: 12px;
	line-height: 1.4;
	max-width: 260px;
}
</style>
